import { Box, Typography, Grid, Container } from '@mui/material';
import { UserCheck, Eye, Package, Award, FileCheck, Home, CheckCircle2 } from 'lucide-react';
import { useEffect } from 'react';
import "aos/dist/aos.css";
import AOS from "aos";

function OperatingStructure() {
  useEffect(() => {
    AOS.init({
      duration: 800,
      once: false,
      offset: 100,
      easing: 'ease-out-cubic',
    });
  }, []);

  const operatingSteps = [
    {
      number: "01",
      icon: UserCheck,
      title: "Project Manager Oversight",
      description: "Dedicated oversight on every build from start to finish"
    },
    {
      number: "02",
      icon: Eye,
      title: "Scheduled Site Inspections",
      description: "Regular inspections at key construction stages"
    },
    {
      number: "03",
      icon: Package,
      title: "Material Quality Verification",
      description: "Rigorous quality checks on all materials used"
    },
    {
      number: "04",
      icon: Award,
      title: "Licensed Trade Certification",
      description: "All trades verified and properly certified"
    },
    {
      number: "05",
      icon: FileCheck,
      title: "NSW Building Compliance",
      description: "Full compliance with NSW building regulations"
    },
    {
      number: "06",
      icon: Home,
      title: "Detailed Handover Inspections",
      description: "Comprehensive final inspection before handover"
    }
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        mt: { xs: 3, md: 5},
        py: { xs: 3, md: 5},
        backgroundColor: 'var(--primary-color)',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-100px',
          right: '-100px',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          backgroundColor: 'rgba(192,174,93,0.05)',
          zIndex: 0
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: '-150px',
          left: '-150px',
          width: '500px',
          height: '500px',
          borderRadius: '50%',
          backgroundColor: 'rgba(192,174,93,0.05)',
          zIndex: 0
        }
      }}
    >
      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        
        {/* Section Header */}
        <Box textAlign="center" mb={{ xs: 6, md: 8 }} data-aos="fade-down">
          <Typography
            sx={{
              fontSize: { xs: '36px', md: '48px' },
              fontWeight: 700,
              color: 'var(--primary-text-color)',
              mb: 2,
              letterSpacing: 1
            }}
          >
            OUR OPERATING STRUCTURE
          </Typography>
          
          <Box
            sx={{
              width: '100px',
              height: '4px',
              backgroundColor: 'var(--primary-text-color)',
              margin: '0 auto 24px'
            }}
          />

          <Typography
            sx={{
              fontSize: { xs: '17px', md: '20px' },
              color: '#ffffff',
              maxWidth: '900px',
              margin: '0 auto',
              lineHeight: 1.7
            }}
          >
            Clarence Homes follows a structured operational framework designed to ensure quality, compliance, and excellence at every stage
          </Typography>
        </Box>

        {/* Operating Steps Grid - 3 in each row */}
        <Grid container spacing={3} justifyContent="center" sx={{ mb: { xs: 6, md: 8 } }}>
          {operatingSteps.map((step, index) => {
            const IconComponent = step.icon;
            return (
              <Grid item xs={12} sm={6} md={4} key={index} sx={{ maxWidth: { md: '380px' }}}>
                <Box
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                  sx={{
                    backgroundColor: '#ffffff',
                    borderRadius: 3,
                    p: 4,
                    height: '100%',
                    minHeight: '220px',
                    position: 'relative',
                    transition: 'all 0.3s ease',
                    border: '2px solid transparent',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: '0 10px 36px rgba(19,25,83,0.13)',
                      borderColor: 'var(--primary-text-color)',
                      '& .step-number': {
                        backgroundColor: 'var(--primary-color)',
                        color: 'var(--primary-text-color)'
                      },
                      '& .step-icon': {
                        backgroundColor: 'var(--primary-color)'
                      },
                      '& .step-icon svg': {
                        color: 'var(--primary-text-color) !important'
                      }
                    }
                  }}
                >
                  {/* Step Number Badge */}
                  <Box
                    className="step-number"
                    sx={{
                      position: 'absolute',
                      top: -15,
                      right: 20,
                      width: 50,
                      height: 50,
                      borderRadius: '50%',
                      backgroundColor: 'var(--primary-text-color)',
                      color: 'var(--primary-color)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '18px',
                      fontWeight: 700,
                      boxShadow: '0 5px 15px rgba(0,0,0,0.2)',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {step.number}
                  </Box>

                  {/* Icon */}
                  <Box
                    className="step-icon"
                    sx={{
                      width: 70,
                      height: 70,
                      borderRadius: '50%',
                      backgroundColor: 'var(--secondary-color)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3,
                      transition: 'all 0.3s ease',
                      '& svg': {
                        color: 'var(--primary-color)',
                        transition: 'color 0.3s ease'
                      }
                    }}
                  >
                    <IconComponent size={32} />
                  </Box>

                  {/* Title */}
                  <Typography
                    className="fw-bold text-second"
                    sx={{
                      fontSize: '19px',
                      mb: 1.5,
                      lineHeight: 1.3
                    }}
                  >
                    {step.title}
                  </Typography>

                  {/* Description */}
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: 'var(--black-light-color)',
                      lineHeight: 1.6
                    }}
                  >
                    {step.description}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>

        {/* Bottom Statement Section */}
        <Box data-aos="zoom-in">
          <Box
            sx={{
              backgroundColor: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              border: '2px solid var(--primary-text-color)',
              p: { xs: 4, md: 6 },
              textAlign: 'center'
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: '18px', md: '22px' },
                color: '#ffffff',
                lineHeight: 1.8,
                mb: 3
              }}
            >
              This systematic approach minimises delays, reduces defects, and ensures each home reflects our core standard:
            </Typography>

            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 2,
                backgroundColor: 'var(--primary-text-color)',
                px: { xs: 3, md: 5 },
                py: { xs: 2, md: 3 },
                borderRadius: 3,
                boxShadow: '0 10px 30px rgba(192,174,93,0.3)'
              }}
            >
              <CheckCircle2 size={32} color="var(--primary-color)" />
              <Typography
                sx={{
                  fontSize: { xs: '22px', md: '32px' },
                  fontWeight: 700,
                  color: 'var(--primary-color)',
                  letterSpacing: 1
                }}
              >
                Raising Benchmarks Everytime
              </Typography>
            </Box>
          </Box>
        </Box>

      </Container>
    </Box>
  );
}

export default OperatingStructure;
